/* eslint-disable react/jsx-no-literals */
import { Box, Button, Drawer } from "@mui/material";
import Image from "next/image";
import { Iconify } from "../../../fe-ui/atoms/Iconify";
import { useCallback, useState } from "react";
import { Map } from "react-map-gl";
import MapMarker from "../../widgets/MapWidget/compounds/map-marker";
import { useTranslation } from "../../../fe-ui/hooks/use-translation";
import { useRouter } from "next/router";
import {
  MAPBOX_API_KEY,
  MAPBOX_SINGLE_UNIT_LOC_DYNAMIC_MAP_THEME,
  MAPBOX_SINGLE_UNIT_LOC_STATIC_MAP_THEME,
} from "../../../config-global";
import { LngLatTuple } from "../../../utils/coords";

type Props = {
  coordinates: LngLatTuple;
  zoom?: number;
  size?: number;
  markerColor?: string;
};

const STATIC_MAP_ROOT = `https://api.mapbox.com/styles/v1/mapbox/${MAPBOX_SINGLE_UNIT_LOC_STATIC_MAP_THEME}/static`;

export const MapWithSignlePin = ({ coordinates, zoom = 12, size = 600, markerColor = "15AF97" }: Props) => {
  const [lng, lat] = coordinates;
  const { pathname } = useRouter();
  const [isShown, setShown] = useState(false);
  const handleClose = useCallback(() => setShown(false), []);
  const handleOpen = useCallback(() => setShown(true), []);
  const isATB = pathname.includes("/embedded/atb/");

  const t = useTranslation();

  return (
    <>
      <Box sx={{ width: "100%", height: 300, position: "relative", overflow: "hidden", borderRadius: 1 }}>
        <Image
          quality={75}
          layout="fill"
          objectFit="cover"
          draggable={false}
          alt="Map with pin"
          // static mapbox map requires longitude, not latitude, as first parameter
          src={`${STATIC_MAP_ROOT}/pin-s-s+${markerColor}(${lng},${lat})/${lng},${lat},${zoom}/${size}x${size}?access_token=${MAPBOX_API_KEY}`}
        />
        <Button
          onClick={handleOpen}
          variant="soft"
          color="inherit"
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
          }}
          size="small"
        >
          {t("cta_map_open")}&nbsp;
          <Iconify icon={"mi:external-link"} />
        </Button>
      </Box>

      <Drawer
        anchor={"right"}
        open={isShown}
        onClose={handleClose}
        slotProps={{
          backdrop: { invisible: false },
        }}
        PaperProps={{
          sx: {
            width: isATB
              ? "100%"
              : {
                  xs: "100%",
                  sm: "75%",
                  md: "50%",
                  lg: "50%",
                  xl: "40%",
                },
          },
        }}
      >
        <Box sx={{ width: "100%", height: "100%" }}>
          <Map
            initialViewState={{
              longitude: lng,
              latitude: lat,
              zoom,
            }}
            mapStyle={`mapbox://styles/mapbox/${MAPBOX_SINGLE_UNIT_LOC_DYNAMIC_MAP_THEME}`}
            mapboxAccessToken={MAPBOX_API_KEY}
          >
            <MapMarker longitude={lng} latitude={lat} />
          </Map>
          <Button
            onClick={handleClose}
            variant="contained"
            color="inherit"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
            }}
          >
            {t("cta_close")}
            &nbsp; &times;
          </Button>
        </Box>
      </Drawer>
    </>
  );
};
